import React, { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { useMutation, useQuery } from '@apollo/client';
import { message, Result, Typography } from 'antd';
import { useNavigate } from 'react-router';
import { useProject, useProjectPhase } from '../hooks';

import { Loader } from '../../../components/UI/Loader';
import OnboardingModal from '../../../components/UI/OnboardingModal';
import { onboardingStyles } from '../../../components/UI/OnboardingModal.styles';
import { mockSiteVisit } from '../../../consts/mockSiteVIsitData';
import { mutations, queries } from '../../../graphql/graphql';
import { GET_SITE_VISIT } from '../../../graphql/queries/siteVisit';
import { useSiteVisit } from '../../../modules/SiteVisit';
import type { ISiteVisit, IMap, ISiteVisitProgress } from '../../../modules/SiteVisit/types';
import type { SitePlanPhaseData } from '../models/SitePlanPhaseModel';

const SiteVisitProvider = lazy(() =>
  import('../../../modules/SiteVisit').then(module => ({ default: module.SiteVisitProvider }))
);
const SiteVisitTool = lazy(() =>
  import('../../../modules/SiteVisit').then(module => ({ default: module.SiteVisitTool }))
);
const SiteVisitView = lazy(() =>
  import('../../../modules/SiteVisit').then(module => ({ default: module.SiteVisitView }))
);

const { Text } = Typography;

interface ProjectSitePlanPageProps {
  phaseId?: string;
}

const buildInitialSiteVisit = (map?: IMap): ISiteVisit => {
  const cloned = JSON.parse(JSON.stringify(mockSiteVisit)) as ISiteVisit;
  if (map) {
    cloned.map = map;
  }
  return cloned;
};

const stripTypename = <T,>(value: T): T => {
  if (Array.isArray(value)) {
    return value.map(item => stripTypename(item)) as T;
  }
  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([key]) => key !== '__typename')
      .map(([key, val]) => [key, stripTypename(val)]);
    return Object.fromEntries(entries) as T;
  }
  return value;
};

const normalizeMap = (map: unknown): IMap | undefined => {
  if (!map || typeof map !== 'object') return undefined;
  const candidate = map as Record<string, unknown>;
  const address = typeof candidate.address === 'string' ? candidate.address : undefined;
  const coordinates = Array.isArray(candidate.coordinates)
    ? (candidate.coordinates as unknown[])
    : undefined;
  const isValidCoordinates =
    Array.isArray(coordinates) &&
    coordinates.length === 2 &&
    coordinates.every(value => typeof value === 'number');
  if (!address || !coordinates) return undefined;
  if (!isValidCoordinates) return undefined;

  const next: IMap = { address, coordinates: coordinates as [number, number] };
  if (typeof candidate.city === 'string') next.city = candidate.city;
  if (typeof candidate.state === 'string') next.state = candidate.state;
  if (typeof candidate.county === 'string') next.county = candidate.county;
  return next;
};

const normalizeSiteVisit = (siteVisit: ISiteVisit): ISiteVisit => {
  const next: ISiteVisit = { ...siteVisit };
  const normalizedMap = normalizeMap(siteVisit.map);
  if (normalizedMap) {
    next.map = normalizedMap;
  } else {
    delete next.map;
  }
  if (!next.preview || typeof next.preview !== 'object' || Array.isArray(next.preview)) {
    delete next.preview;
  }
  return next;
};

const useSitePlanOrderState = ({
  sitePlanOrderId,
  sitePlanOrderStatus,
  sitePlanOrderLoading,
}: {
  sitePlanOrderId?: string;
  sitePlanOrderStatus?: string;
  sitePlanOrderLoading: boolean;
}) => {
  const hasSitePlanOrder = Boolean(sitePlanOrderId);
  const isResolvingSitePlanOrder = hasSitePlanOrder && sitePlanOrderLoading;
  const isWaitingForSitePlan =
    hasSitePlanOrder && Boolean(sitePlanOrderStatus) && sitePlanOrderStatus !== 'DONE';

  return { isWaitingForSitePlan, isResolvingSitePlanOrder };
};

const useSiteVisitEditingState = ({
  phaseReady,
  siteVisitId,
  isWaitingForSitePlan,
  isResolvingSitePlanOrder,
}: {
  phaseReady: boolean;
  siteVisitId?: string;
  isWaitingForSitePlan: boolean;
  isResolvingSitePlanOrder: boolean;
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const initialSiteVisitIdRef = useRef<string | null | undefined>(undefined);
  const didInitEditRef = useRef(false);

  if (phaseReady && initialSiteVisitIdRef.current === undefined) {
    initialSiteVisitIdRef.current = siteVisitId ?? null;
  }

  useEffect(() => {
    if (didInitEditRef.current) return;
    if (initialSiteVisitIdRef.current === undefined) return;
    if (!initialSiteVisitIdRef.current && !isWaitingForSitePlan && !isResolvingSitePlanOrder) {
      setIsEditing(true);
    }
    didInitEditRef.current = true;
  }, [isResolvingSitePlanOrder, isWaitingForSitePlan, siteVisitId]);

  useEffect(() => {
    if (isWaitingForSitePlan) {
      setIsEditing(false);
    }
  }, [isWaitingForSitePlan]);

  return { isEditing, setIsEditing };
};

const buildSitePlanOrderPhaseData = (
  phaseData: SitePlanPhaseData,
  order: { id: string }
): SitePlanPhaseData => ({
  ...phaseData,
  sitePlanOrderId: order.id,
});

const SiteVisitIdSync: React.FC<{
  phaseData: SitePlanPhaseData;
  setData: (data: unknown) => void;
  savePhase: (patch?: Partial<unknown>, options?: { omitIsActive?: boolean }) => Promise<void>;
}> = ({ phaseData, setData, savePhase }) => {
  const { siteVisit } = useSiteVisit();
  const lastSavedIdRef = useRef<string | null>(null);

  React.useEffect(() => {
    if (!siteVisit?.id) return;
    if (phaseData.siteVisitId === siteVisit.id) {
      lastSavedIdRef.current = siteVisit.id;
      return;
    }
    if (lastSavedIdRef.current === siteVisit.id) return;

    const nextData: SitePlanPhaseData = { ...phaseData, siteVisitId: siteVisit.id };
    setData(nextData);
    savePhase({ data: nextData }, { omitIsActive: true });
    lastSavedIdRef.current = siteVisit.id;
  }, [phaseData, savePhase, setData, siteVisit?.id]);

  return null;
};

export const ProjectSitePlanPage: React.FC<ProjectSitePlanPageProps> = ({ phaseId }) => {
  const { phaseId: routePhaseId } = useParams();
  const navigate = useNavigate();
  const resolvedPhaseId = phaseId || routePhaseId;
  const { getPreviousPhase, getNextPhase, persistActivePhase, project } = useProject();
  const { phase, setData, savePhase, loading } = useProjectPhase(resolvedPhaseId);
  const hasBootstrappedRef = useRef(false);
  const lastProgressRef = useRef<number | null>(null);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [createSitePlanOrder, { loading: creatingOrder }] = useMutation(
    mutations.order.SITE_PLAN_ORDER_CREATE
  );

  const phaseData = useMemo(() => (phase?.data || {}) as SitePlanPhaseData, [phase?.data]);
  const siteVisitId = phaseData.siteVisitId;
  const sitePlanOrderId = phaseData.sitePlanOrderId;
  const {
    data: sitePlanOrderData,
    startPolling,
    stopPolling,
    loading: sitePlanOrderLoading
  } = useQuery(queries.order.ORDER, {
    variables: { id: sitePlanOrderId },
    skip: !sitePlanOrderId,
    fetchPolicy: 'network-only',
  });
  const sitePlanOrderStatus = sitePlanOrderData?.order?.status as string | undefined;
  const { isWaitingForSitePlan, isResolvingSitePlanOrder } = useSitePlanOrderState({
    sitePlanOrderId,
    sitePlanOrderStatus,
    sitePlanOrderLoading
  });

  useEffect(() => {
    if (!sitePlanOrderId) return;
    if (sitePlanOrderStatus === 'DONE') {
      stopPolling();
      return;
    }
    startPolling(30000);
    return () => stopPolling();
  }, [sitePlanOrderId, sitePlanOrderStatus, startPolling, stopPolling]);
  const { isEditing, setIsEditing } = useSiteVisitEditingState({
    phaseReady: Boolean(phase),
    siteVisitId,
    isWaitingForSitePlan,
    isResolvingSitePlanOrder,
  });

  const previousPhase = useMemo(
    () => (resolvedPhaseId ? getPreviousPhase(resolvedPhaseId) : null),
    [getPreviousPhase, resolvedPhaseId]
  );

  useEffect(() => {
    if (typeof phase?.progress !== 'number') return;
    lastProgressRef.current = phase.progress;
  }, [phase?.progress]);

  const handleProgressUpdate = useCallback(
    (progress: ISiteVisitProgress) => {
      if (!phase) return;
      const nextProgress = Math.max(0, Math.min(100, progress.percentage));
      if (lastProgressRef.current === nextProgress) return;
      lastProgressRef.current = nextProgress;
      savePhase({ progress: nextProgress }, { omitIsActive: true }).catch(error => {
        console.error('Site plan progress update error', error);
      });
    },
    [phase, savePhase]
  );

  const hasSiteVisit = Boolean(siteVisitId);
  const showSummaryView = hasSiteVisit && !isEditing && !isWaitingForSitePlan;

  const nextPhase = useMemo(() => {
    if (!phase) return null;
    return getNextPhase(phase.id);
  }, [getNextPhase, phase]);

  const initialMap = useMemo<IMap | undefined>(() => {
    const address = previousPhase?.data?.address;
    const coordinates = previousPhase?.data?.coordinates;
    if (!address || !coordinates) return undefined;
    return {
      address,
      coordinates,
      city: previousPhase?.data?.city,
      state: previousPhase?.data?.state,
      county: previousPhase?.data?.county,
    } as IMap;
  }, [previousPhase]);

  const { data: siteVisitData, loading: siteVisitLoading } = useQuery(GET_SITE_VISIT, {
    variables: { id: siteVisitId },
    skip: !isEditing || !siteVisitId || isWaitingForSitePlan,
    fetchPolicy: 'network-only',
  });

  const fallbackSiteVisit = useMemo(
    () => normalizeSiteVisit(buildInitialSiteVisit(initialMap)),
    [initialMap]
  );
  const cleanedSiteVisit = useMemo(() => {
    if (!siteVisitData?.siteVisit) return null;
    const stripped = stripTypename(siteVisitData.siteVisit);
    return normalizeSiteVisit(stripped as ISiteVisit);
  }, [siteVisitData?.siteVisit]);
  const initialSiteVisit =
    siteVisitId && cleanedSiteVisit ? (cleanedSiteVisit as ISiteVisit) : fallbackSiteVisit;

  const handleSummaryNext = useCallback(async () => {
    if (!phase || !nextPhase) return;
    const progressValue = typeof phase.progress === 'number' ? phase.progress : 0;
    if (progressValue < 80) {
      setShowProgressModal(true);
      return;
    }
    try {
      await savePhase({ progress: 100, status: 'completed', isActive: false });
      await persistActivePhase(nextPhase.id, { locked: false });
      if (project) {
        navigate(`/project/${project.id}/phase/${nextPhase.id}`);
      }
    } catch (error) {
      message.error('Failed to complete site plan phase');
      console.error('Site plan phase completion error', error);
    }
  }, [navigate, nextPhase, persistActivePhase, phase, project, savePhase]);

  const handleDoItForYou = useCallback(async () => {
    if (!project?.id || !phase) return;
    if (creatingOrder) return;
    if (sitePlanOrderId) {
      message.info('Site plan request already submitted.');
      return;
    }

    try {
      const { data: mutationData } = await createSitePlanOrder({
        variables: { projectId: project.id },
      });
      const order = mutationData?.sitePlanOrderCreate;
      if (!order?.id) {
        message.error('Failed to create site plan request.');
        return;
      }

      const nextData = buildSitePlanOrderPhaseData(phaseData, order);
      setData(nextData);
      await savePhase({ data: nextData }, { omitIsActive: true });
      setIsEditing(false);
      message.success('Site plan request received. We will contact you soon.');
    } catch (error: any) {
      message.error(error?.message || 'Failed to create site plan request.');
    }
  }, [
    createSitePlanOrder,
    creatingOrder,
    phase,
    phaseData,
    project?.id,
    savePhase,
    setData,
    sitePlanOrderId,
  ]);

  if (loading) {
    return <Loader />;
  }

  if (isResolvingSitePlanOrder) {
    return <Loader />;
  }

  if (isWaitingForSitePlan) {
    return (
      <Result
        status="info"
        title="Site plan request received"
        subTitle="Please wait while our team prepares your site plan. We will contact you soon."
        extra={sitePlanOrderId ? <Text type="secondary">Request ID: {sitePlanOrderId}</Text> : null}
      />
    );
  }

  if (!isEditing && !siteVisitId) {
    return <Loader />;
  }

  if (showSummaryView) {
    return (
      <Suspense fallback={<Loader />}>
        <SiteVisitView
          id={siteVisitId ?? undefined}
          onEdit={() => setIsEditing(true)}
          showShare={false}
          onNext={nextPhase ? handleSummaryNext : undefined}
        />
        <OnboardingModal
          open={showProgressModal}
          onClose={() => setShowProgressModal(false)}
          onButtonClick={() => setIsEditing(true)}
          onSecondButtonClick={handleDoItForYou}
          image="/public/images/onboarding_site_visit.png"
          title="Finish Your Site Plan to Continue"
          badge="Site Plan Progress"
          buttonText="Resume site plan"
          secondButtonText="Do It For You"
          content={
            <>
              <p className={onboardingStyles.paragraph}>
                To request a proposal, please complete at least{' '}
                <span className={onboardingStyles.highlight}>80%</span> of your site plan.
              </p>
              <p className={onboardingStyles.paragraph}>
                The more complete your site plan details are, the more accurate and reliable your
                proposal will be.
              </p>
              <p className={onboardingStyles.paragraph}>
                If unsure how to complete this phase, reach out. We have a “Do It For You” service
                available.
              </p>
            </>
          }
        />
      </Suspense>
    );
  }

  const shouldBlockOnSiteVisit = isEditing && Boolean(siteVisitId) && !hasBootstrappedRef.current;
  if (shouldBlockOnSiteVisit && siteVisitLoading) {
    return <Loader />;
  }

  if (shouldBlockOnSiteVisit && !siteVisitLoading && !siteVisitData?.siteVisit) {
    return <div>Site visit not found</div>;
  }

  if (!phase) return null;

  hasBootstrappedRef.current = true;

  return (
    <Suspense fallback={<Loader />}>
      <SiteVisitProvider initialSiteVisit={initialSiteVisit} onProgress={handleProgressUpdate}>
        <SiteVisitIdSync phaseData={phaseData} setData={setData} savePhase={savePhase} />
        <SiteVisitTool redirectOnComplete={false} onComplete={() => setIsEditing(false)} />
      </SiteVisitProvider>
    </Suspense>
  );
};
